import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Bell, CheckCheck, Settings2, Loader2 } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '../ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';
import { notificationsAPI, fmtWhen } from '../../lib/communicationsAPI';

const typeLabel = (t) => (t || 'other').replace(/_/g, ' ');

// Notification centre (staff + clients): unread count (15s polling), read/unread + type filters, mark all read, click-through.
export const NotificationBell = () => {
  const navigate = useNavigate();
  const [unread, setUnread] = useState(0);
  const [items, setItems] = useState(null);
  const [open, setOpen] = useState(false);
  const [onlyUnread, setOnlyUnread] = useState(false);
  const [type, setType] = useState('all');

  const refresh = useCallback(() => notificationsAPI.unreadCount().then((r) => setUnread(r.data.unread)).catch(() => {}), []);
  const loadList = useCallback(() => notificationsAPI.list({ limit: 50 }).then((r) => { setItems(r.data.notifications); setUnread(r.data.unread); }).catch(() => setItems([])), []);
  useEffect(() => {
    refresh();
    const t = setInterval(refresh, 15000);
    const evts = ['comms:changed', 'notifications:changed'];
    evts.forEach((e) => window.addEventListener(e, refresh));
    return () => { clearInterval(t); evts.forEach((e) => window.removeEventListener(e, refresh)); };
  }, [refresh]);
  useEffect(() => { if (open) loadList(); }, [open, loadList]);

  const openItem = async (n) => {
    if (!n.read_at) await notificationsAPI.read(n.id).catch(() => {});
    setOpen(false); window.dispatchEvent(new Event('notifications:changed'));
    if (n.link) navigate(n.link);
  };
  const readAll = async () => { await notificationsAPI.readAll().catch(() => {}); loadList(); window.dispatchEvent(new Event('notifications:changed')); };
  const types = [...new Set((items || []).map((n) => n.type || 'other'))];
  const shown = (items || []).filter((n) => (!onlyUnread || !n.read_at) && (type === 'all' || (n.type || 'other') === type));
  const chip = (active) => cn('rounded-full px-2.5 py-0.5 text-xs capitalize transition-colors ease-ui', active ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground hover:text-foreground');

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button variant="ghost" size="icon" className="relative h-9 w-9 text-muted-foreground hover:text-foreground" aria-label={`Notifications${unread ? ` (${unread} unread)` : ''}`} data-testid="notification-bell">
          <Bell className="h-4 w-4" strokeWidth={1.6} />
          {unread > 0 && <span className="absolute -right-0.5 -top-0.5 flex h-[18px] min-w-[18px] items-center justify-center rounded-full bg-destructive px-1 text-[10px] font-semibold text-white" data-testid="notification-bell-count">{unread > 99 ? '99+' : unread}</span>}
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-[380px] p-0" data-testid="notification-dropdown">
        <div className="flex items-center justify-between border-b border-border px-3 py-2">
          <span className="text-sm font-semibold">Notifications</span>
          <div className="flex items-center gap-1">
            <Button size="sm" variant="ghost" className="h-7 px-2 text-xs" onClick={readAll} disabled={!unread} data-testid="notification-read-all"><CheckCheck className="mr-1 h-3.5 w-3.5" />Mark all read</Button>
            <Button size="sm" variant="ghost" className="h-7 w-7 p-0" onClick={() => { setOpen(false); navigate('/settings?tab=communications'); }} aria-label="Notification preferences" data-testid="notification-prefs-btn"><Settings2 className="h-3.5 w-3.5" /></Button>
          </div>
        </div>
        <div className="flex flex-wrap gap-1.5 border-b border-border px-3 py-2" data-testid="notification-filters">
          <button type="button" className={chip(!onlyUnread)} onClick={() => setOnlyUnread(false)} data-testid="notification-filter-all">All</button>
          <button type="button" className={chip(onlyUnread)} onClick={() => setOnlyUnread(true)} data-testid="notification-filter-unread">Unread</button>
          {types.length > 1 && <span className="mx-1 w-px bg-border" />}
          {types.length > 1 && ['all', ...types].map((t) => <button key={t} type="button" className={chip(type === t)} onClick={() => setType(t)} data-testid={`notification-type-${t}`}>{t === 'all' ? 'All types' : typeLabel(t)}</button>)}
        </div>
        <div className="max-h-[420px] overflow-y-auto">
          {items === null ? <Loader2 className="mx-auto my-6 h-5 w-5 animate-spin" /> : shown.length === 0 ? (
            <p className="py-8 text-center text-sm text-muted-foreground" data-testid="notification-empty">{items.length ? 'Nothing matches these filters.' : "You're all caught up."}</p>
          ) : shown.map((n) => (
            <button key={n.id} type="button" onClick={() => openItem(n)} data-testid={`notification-item-${n.id}`}
              className={cn('flex w-full gap-2.5 border-b border-border px-3 py-2.5 text-left hover:bg-muted/60', !n.read_at && 'bg-primary/[0.04]')}>
              <span className={cn('mt-1.5 h-2 w-2 shrink-0 rounded-full', n.read_at ? 'bg-transparent' : 'bg-primary')} />
              <span className="min-w-0 flex-1">
                <span className={cn('block truncate text-sm', !n.read_at && 'font-semibold')}>{n.title}</span>
                {n.body && <span className="block truncate text-xs text-muted-foreground">{n.body}</span>}
                <span className="mt-0.5 block text-[11px] capitalize text-muted-foreground">{typeLabel(n.type)}</span>
              </span>
              <span className="shrink-0 text-[10px] text-muted-foreground">{fmtWhen(n.created_at)}</span>
            </button>))}
        </div>
      </PopoverContent>
    </Popover>
  );
};
